const { $ } = window;

/**
 * Volets du tunnel d'ajout de commande.
 *
 * Les étapes vivent sur la MÊME page : le panier reste verrouillé par l'ajax de l'étape 1
 * (`event_forms/addAdvanced`), et son expiration se calcule sur `payment.created_at +
 * ticketing_setting.minutes_lock` — le découpage en volets ne déplace donc aucune échéance.
 * Il n'y a qu'un seul POST, à la dernière étape.
 */
window.YP.AddOrderWizard = () => ({
    maxStep: 1,

    init() {
        this.$root = $('.js-order-wizard');
        if (!this.$root.length) {
            return;
        }

        this.bindStepIndicator();
        this.bindNextButton();
        this.bindCollapsibleToggles();
        this.bindErrorReset();
        this.bindFormLoaded();
        this.bindSendOrderRecipient();
        this.initCollapsibles();
        this.goTo(this.initialStep());
    },

    panes() {
        return this.$root.find('.js-order-wizard__pane');
    },

    pane(step) {
        return this.$root.find(`.js-order-wizard__pane[data-step="${step}"]`);
    },

    lastStep() {
        const steps = this.panes()
            .map((index, pane) => parseInt($(pane).data('step'), 10))
            .get();

        return steps.length ? Math.max(...steps) : 1;
    },

    /**
     * Au retour d'une validation serveur en échec, la page est rendue avec le formulaire complet :
     * on rouvre le volet qui porte la première erreur plutôt que de renvoyer l'organisateur au
     * choix des billets.
     */
    initialStep() {
        if (!this.pane(2).length) {
            return 1;
        }

        const $errored = this.panes().filter(
            (index, pane) => $(pane).find('.c-form-item--error').length > 0,
        );

        return $errored.length ? parseInt($errored.first().data('step'), 10) : 2;
    },

    bindStepIndicator() {
        $(document).on('click', '.js-order-wizard__steps [data-step]', (e) => {
            const step = parseInt($(e.currentTarget).data('step'), 10);
            // Une étape non encore atteinte n'est pas cliquable : on n'y saute pas sans avoir
            // verrouillé le panier ni rempli ce qui précède.
            if (step > this.maxStep) {
                return;
            }
            // Revenir en arrière est toujours permis : on ne bloque que la fuite en avant.
            if (step > this.currentStep && !this.validatePane(this.currentStep)) {
                this.focusFirstError();
                return;
            }

            this.goTo(step);
        });
    },

    bindNextButton() {
        $(document).on('click', '.js-order-wizard__next', (e) => {
            e.preventDefault();

            if (!this.validatePane(this.currentStep)) {
                this.focusFirstError();
                return;
            }

            this.goTo(this.currentStep + 1);
        });
    },

    bindCollapsibleToggles() {
        $(document).on('change', '.js-order-wizard__collapsible-toggle', (e) => {
            const $toggle = $(e.currentTarget);
            this.toggleCollapsible($toggle, $toggle.is(':checked'));
        });
    },

    /**
     * Une erreur signalée ici n'est pas celle du serveur : elle disparaît dès que la réponse est
     * donnée, sans attendre une soumission.
     */
    bindErrorReset() {
        $(document).on('input change', '[data-client-error] :input', (e) => {
            const $item = $(e.target).closest('[data-client-error]');

            if (this.isFilled($item)) {
                this.clearError($item);
            }
        });
    },

    /**
     * Une section s'ouvre d'office quand elle ne peut pas rester repliée : une réponse obligatoire
     * ne se saisit pas dans un bloc masqué, et une erreur de validation doit être visible là où
     * elle se corrige. `data-must-open` vient du serveur — ni le caractère obligatoire d'une
     * question sf1 ni une erreur de formulaire imbriqué ne se lisent depuis le DOM.
     */
    initCollapsibles() {
        this.$root.find('.js-order-wizard__collapsible-toggle').each((index, toggle) => {
            const $toggle = $(toggle);
            const $body = this.collapsibleBody($toggle);
            // Un aller-retour de validation ne défait pas le choix de l'organisateur, dans un sens
            // comme dans l'autre : le champ posté prime sur l'ouverture d'office, et une section
            // déjà renseignée reste dépliée — repliée, ses champs désactivés ne seraient plus
            // postés et la commande partirait sans eux.
            const skipped = this.skipField($toggle).val() === '1';
            const isOpen = !skipped && ($body.attr('data-must-open') === '1' || this.hasAnswer($body));

            $toggle.prop('checked', isOpen);
            this.toggleCollapsible($toggle, isOpen);
        });
    },

    /**
     * Les quantités d'activité sont rendues à `0` : seule une quantité positive est une saisie.
     */
    hasAnswer($scope) {
        return $scope
            .find('input:not([type="hidden"]):not([data-locked]), select, textarea')
            .toArray()
            .some((field) => {
                if (field.type === 'checkbox' || field.type === 'radio') {
                    return field.checked;
                }

                const value = String(field.value || '').trim();

                return field.name.endsWith('[quantity]') ? Number(value) > 0 : value !== '';
            });
    },

    collapsibleBody($toggle) {
        return $toggle.closest('.c-container').find('.js-order-wizard__collapsible-body');
    },

    /**
     * Champ qui porte jusqu'au serveur le fait qu'une section reste repliée : lui seul rend les
     * réponses concernées facultatives, sans quoi la commande reviendrait en erreur.
     */
    skipField($toggle) {
        const id = $toggle.attr('data-skip-field');

        return id ? $(document.getElementById(id)) : $();
    },

    /**
     * Replié, le bloc est aussi désactivé : un champ requis mais masqué empêcherait le navigateur
     * de soumettre le formulaire (le contrôle invalide n'est pas focusable), et un champ actif
     * posterait une réponse vide. Les hidden portent le binding sf1 (ids, csrf) — jamais touchés,
     * pas plus que les sessions complètes, désactivées par le serveur.
     */
    toggleCollapsible($toggle, isOpen) {
        const $card = $toggle.closest('.c-container');
        const $body = this.collapsibleBody($toggle);

        $body.toggle(isOpen);
        // Le filet sépare le titre du détail : replié, il n'y a plus rien à séparer.
        $card.find('.js-order-wizard__collapsible-divider').toggle(isOpen);
        $body
            .find('input:not([type="hidden"]):not([data-locked]), select, textarea')
            .prop('disabled', !isOpen);

        this.skipField($toggle).val(isOpen ? '0' : '1');
    },

    /**
     * Le caractère obligatoire d'une réponse ne vit que côté serveur (`is_required` est une option
     * de widget sf1) : les gabarits le recopient en `data-required`, seul repère lisible ici.
     */
    emptyRequiredItems($scope) {
        return $scope.find('[data-required="1"]').filter((index, item) => !this.isFilled($(item)));
    },

    isFilled($item) {
        const $fields = $item.find('input, select, textarea').not('[type="hidden"]');
        const $choices = $fields.filter('[type="checkbox"], [type="radio"]');

        if ($choices.length) {
            return $choices.filter(':checked').length > 0;
        }

        return $fields.toArray().some((field) => String(field.value || '').trim() !== '');
    },

    /**
     * Ce qui est replié est hors du champ de la validation : l'organisateur a explicitement remis
     * ces réponses à plus tard, et le serveur les accepte vides.
     */
    validatePane(step) {
        const $pane = this.pane(step);

        $pane.find('[data-client-error]').each((index, item) => this.clearError($(item)));

        const $empty = this.emptyRequiredItems($pane).filter(':visible');
        $empty.each((index, item) => this.markError($(item)));

        return $empty.length === 0;
    },

    markError($item) {
        const $message = $item.find('.c-form-info--error').first();

        $item.attr('data-client-error', '1').addClass('c-form-item--error');

        if ($message.length) {
            $message.text('Merci de remplir ce champ.');
        }
    },

    clearError($item) {
        const $message = $item.find('.c-form-info--error').first();

        // Appelé sur les seuls items que nous avons marqués : une erreur venue du serveur reste
        // affichée tant que la commande n'a pas été rejouée.
        $item.removeAttr('data-client-error').removeClass('c-form-item--error');

        if ($message.length) {
            $message.text('');
        }
    },

    focusFirstError() {
        const $first = this.$root.find('[data-client-error]').filter(':visible').first();

        if (!$first.length) {
            return;
        }

        $first.get(0).scrollIntoView({ behavior: 'smooth', block: 'center' });
        $first.find(':input').not('[type="hidden"]').first().trigger('focus');
    },

    /**
     * L'étape 1 injecte les volets suivants par ajax (order.js). On se branche sur l'événement
     * global jQuery plutôt que d'observer le DOM : il est émis une fois les volets insérés.
     */
    bindFormLoaded() {
        const ajaxAction = this.$root.attr('ajax-action');

        $(document).ajaxSuccess((event, xhr, settings) => {
            if (settings.url !== ajaxAction) {
                return;
            }

            this.initCollapsibles();
            // Panier vide ou refusé : l'ajax ne renvoie rien, on reste au choix des billets.
            this.goTo(this.pane(2).length ? 2 : 1);
        });
    },

    /**
     * L'envoi de la commande exige un email (`AddOrderAdvancedForm::processFormValidation`) : sans
     * lui, la case est bloquée ici plutôt que de renvoyer l'organisateur au volet « Informations »
     * après un aller-retour serveur.
     */
    bindSendOrderRecipient() {
        $(document).on('input change', '#addsell_email_address', () => this.refreshSendOrderRecipient());

        $(document).on('click', '.js-order-wizard__goto', (e) => {
            e.preventDefault();
            this.goTo(parseInt($(e.currentTarget).data('step'), 10));
        });
    },

    refreshSendOrderRecipient() {
        const $checkbox = this.$root.find('.js-send-order');
        if (!$checkbox.length) {
            return;
        }

        const email = String(this.$root.find('#addsell_email_address').val() || '').trim();
        const $recipient = this.$root.find('.js-send-order-recipient');

        if (!email && $checkbox.is(':checked')) {
            // `change` pour que order.js masque aussi le choix de la langue du mail.
            $checkbox.prop('checked', false).trigger('change');
        }
        $checkbox.prop('disabled', !email);

        $recipient.text(email ? `${$recipient.attr('data-prefix')} ${email}` : '').toggle(!!email);
        this.$root.find('.js-send-order-missing-email').toggle(!email);
    },

    goTo(step) {
        this.refreshSendOrderRecipient();
        this.currentStep = step;
        this.maxStep = Math.max(this.maxStep, step);

        this.panes().hide();
        this.pane(step).show();

        // `#js-form-addsell` enveloppe les volets 2 et 3 : le laisser visible sur l'étape 1
        // afficherait les boutons de fin de tunnel.
        $('#js-form-addsell').toggle(step > 1);

        this.$root.find('.js-order-wizard__steps [data-step]').each((index, item) => {
            const $item = $(item);
            const itemStep = parseInt($item.data('step'), 10);

            $item
                .toggleClass('c-progress-indicator__item--current', itemStep === step)
                .toggleClass('c-progress-indicator__item--complete', itemStep < step);
        });

        const isLastStep = step === this.lastStep();
        this.$root.find('.js-order-wizard__next').toggle(!isLastStep);
        this.$root.find('.js-orders__show-add-btn').toggle(isLastStep);
    },
});

window.addEventListener('load', () => window.YP.AddOrderWizard().init());
